/**
 * The query the operations dashboard's map asks the site-map endpoint for:
 * the page's own filter vocabulary (a time window, plants, structure type,
 * alarm state) translated into the endpoint's wire names.
 *
 * Contains:
 * - buildOperationsMapParams — dashboard filter state -> map endpoint query
 *
 * Returns plain JS — no JSX.
 */

// The endpoint is viewport-scoped, but this map clusters client-side and wants
// the whole filtered set, so it asks for the entire world. `zoom_level` tells
// the backend how coarsely it may collapse points — 15 is past its clustering
// threshold, so it returns every row.
const WORLD_BOUNDS = Object.freeze({ min_lat: -90, max_lat: 90, min_lon: -180, max_lon: 180 });
const UNTHINNED_ZOOM_LEVEL = 15;

// Dashboard filter name -> the map endpoint's query parameter. Only filters the
// page has actually set are sent; an unset filter means "no narrowing", and so
// does a zero-hour window — the page's "all time" choice.
const PARAM_BY_FILTER = Object.freeze({
  siteGroupId: 'site_group',
  alarmStatus: 'alarm_status',
  alarmState: 'alarm_state',
  onlineStatus: 'online_status',
  hours: 'hours',
  structureType: 'structure_type',
  category: 'category',
});

/**
 * Build the map endpoint query for one customer and the dashboard's filters.
 *
 * @param {string|number} customerId - the customer whose sites to map.
 * @param {object} [filters] - the dashboard's map filter state.
 * @returns {object} query params for `fetchDashboardMapSites`.
 */
export default function buildOperationsMapParams(customerId, filters) {
  const params = { customer: customerId, zoom_level: UNTHINNED_ZOOM_LEVEL, ...WORLD_BOUNDS };
  Object.entries(PARAM_BY_FILTER).forEach(([key, param]) => {
    const value = filters?.[key];
    if (value) params[param] = value;
  });
  return params;
}
